import { useId, useState } from "react";
import { useMutation } from "convex/react";
import { Button, Dialog, Input, Label } from "@repo/ui";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { messageErreur } from "./edition";

export type EnTete = {
  _id: Id<"scorecards">;
  titre: string;
};

export function DialogueDupliquer({
  source,
  onFermer,
  onDupliquee,
}: {
  source: EnTete;
  onFermer: () => void;
  onDupliquee: (id: Id<"scorecards">) => void;
}) {
  const id = useId();
  const dupliquer = useMutation(api.scorecards.dupliquerScorecard);
  const [titre, setTitre] = useState(`${source.titre} (copie)`);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      onDupliquee(await dupliquer({ scorecardId: source._id, titre }));
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog isOpen onClose={onFermer} title="Dupliquer la scorecard" className="max-w-lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void valider();
        }}
        className="space-y-4"
      >
        <p className="text-sm text-muted-foreground">
          La copie est indépendante : la modifier ne change pas l'originale.
        </p>
        <div>
          <Label htmlFor={id} className="mb-1">
            Titre de la copie
          </Label>
          <Input id={id} value={titre} onChange={(e) => setTitre(e.target.value)} />
        </div>
        {erreur && (
          <p role="alert" className="text-sm text-destructive">
            {erreur}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onFermer}>
            Annuler
          </Button>
          <Button type="submit" disabled={enCours || !titre.trim()}>
            Dupliquer
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Change le nom sans créer de version : rien à republier. */
export function DialogueRenommer({
  source,
  onFermer,
}: {
  source: EnTete;
  onFermer: () => void;
}) {
  const id = useId();
  const renommer = useMutation(api.scorecards.renommerScorecard);
  const [titre, setTitre] = useState(source.titre);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await renommer({ scorecardId: source._id, titre });
      onFermer();
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog isOpen onClose={onFermer} title="Renommer" className="max-w-lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void valider();
        }}
        className="space-y-4"
      >
        <p className="text-sm text-muted-foreground">
          Le nom change tout de suite, sans nouvelle version ni publication. Les
          campagnes déjà créées et les copies déjà assignées gardent l'ancien nom.
        </p>
        <div>
          <Label htmlFor={id} className="mb-1">
            Nom
          </Label>
          <Input id={id} value={titre} onChange={(e) => setTitre(e.target.value)} />
        </div>
        {erreur && (
          <p role="alert" className="text-sm text-destructive">
            {erreur}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onFermer}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={enCours || !titre.trim() || titre.trim() === source.titre}
          >
            Renommer
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Suppression définitive d'un brouillon jamais publié. Le serveur refuse
 * toute scorecard publiée, même une fois : elle s'archive.
 */
export function DialogueSupprimer({
  source,
  onFermer,
}: {
  source: EnTete;
  onFermer: () => void;
}) {
  const supprimer = useMutation(api.scorecards.supprimerScorecard);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const confirmer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await supprimer({ scorecardId: source._id });
      onFermer();
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog isOpen onClose={onFermer} title="Supprimer le brouillon ?" className="max-w-lg">
      <p className="mb-4 text-sm text-muted-foreground">
        « {source.titre} » n'a jamais été publiée : elle est supprimée
        définitivement, avec tout son historique. Cette action est irréversible.
      </p>
      {erreur && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {erreur}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onFermer}>
          Annuler
        </Button>
        <Button variant="destructive" disabled={enCours} onClick={() => void confirmer()}>
          Supprimer
        </Button>
      </div>
    </Dialog>
  );
}
